import React, { useState, useCallback, useRef } from 'react';
import { ClipboardCheck, CheckCircle2, Circle, Square, CheckSquare, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const ExerciseElement = ({
  element,
  isEditor = false,
  studentView = false,
  selectedAnswers = [],
  showResult = false,
  isCorrect = null,
  correctAnswers = null,
  onSelectAnswer,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, type } = element;
  const {
    exerciseType,
    question,
    options = [],
    allowRetry,
    fontFamily,
    fontSize,
    textColor,
    questionTextColor,
    questionBgColor,
    optionsTextColor,
    optionsBgColor,
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const effectiveQuestionTextColor = questionTextColor || textColor;
  const effectiveOptionsTextColor = optionsTextColor || textColor;

  const questionTextStyle = {
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontSize ? { fontSize: `${fontSize}px` } : {}),
    ...(effectiveQuestionTextColor ? { color: effectiveQuestionTextColor } : {}),
  };
  const optionTextStyle = {
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontSize ? { fontSize: `${fontSize}px` } : {}),
    ...(effectiveOptionsTextColor ? { color: effectiveOptionsTextColor } : {}),
  };
  const questionBgStyle = questionBgColor ? { backgroundColor: questionBgColor } : {};
  const optionBaseBgStyle = optionsBgColor ? { backgroundColor: optionsBgColor } : {};

  const isSingleChoice = exerciseType === 'singleChoice' || type === 'singleChoiceExercise';
  const isMultipleChoice = exerciseType === 'multipleChoice' || type === 'multipleChoiceExercise';

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: options.length,
    onAutoGrow,
  });

  // Filter out isCorrect from options if studentView
  const safeOptions = studentView
    ? options.map(({ isCorrect: _, ...opt }) => opt)
    : options;

  // Editor preview mode - render same as interactive but non-functional
  if (isEditor) {
    const Icon = isSingleChoice ? ClipboardCheck : CheckCircle2;
    const colorClass = isSingleChoice ? 'emerald' : 'teal';

    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        {/* Question */}
        {question && (
          <div className="px-4 py-3 border-b border-gray-100" style={questionBgStyle}>
            <p className="text-sm font-medium text-gray-800 leading-relaxed" style={questionTextStyle}>
              {question}
            </p>
          </div>
        )}

        {/* Options */}
        <div ref={scrollRef} className="flex-1 p-3 overflow-y-auto space-y-2">
          {options.map((option, idx) => {
            const optionIsCorrect = option.isCorrect;

            // Highlight correct answers in editor
            let optionBg = 'bg-white';
            let optionBorder = 'border-gray-200';
            let optionText = 'text-gray-700';
            let optionInlineBg = optionBaseBgStyle;

            if (optionIsCorrect) {
              optionBg = isSingleChoice ? 'bg-emerald-50' : 'bg-teal-50';
              optionBorder = isSingleChoice ? 'border-emerald-400' : 'border-teal-400';
              optionText = isSingleChoice ? 'text-emerald-700' : 'text-teal-700';
              optionInlineBg = {};
            }

            return (
              <div
                key={option.id || idx}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 ${optionBorder} ${optionBg}`}
                style={optionInlineBg}
              >
                {/* Selection indicator */}
                <div className="flex-shrink-0">
                  {isSingleChoice ? (
                    optionIsCorrect ? (
                      <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    ) : (
                      <Circle size={16} className="text-gray-400" />
                    )
                  ) : (
                    optionIsCorrect ? (
                      <CheckSquare size={16} className="text-teal-500" />
                    ) : (
                      <Square size={16} className="text-gray-400" />
                    )
                  )}
                </div>

                {/* Option text */}
                <span className={`flex-1 text-left text-sm ${optionText}`} style={optionTextStyle}>
                  {option.text || t('games.exercise.optionFallback', { index: idx + 1 })}
                </span>
              </div>
            );
          })}
        </div>

      </div>
    );
  }

  // Interactive/Classroom mode
  const handleOptionClick = (optionId) => {
    if (disabled || showResult) return;

    if (isSingleChoice) {
      onSelectAnswer?.([optionId]);
    } else {
      // Multiple choice - toggle selection
      const newSelected = selectedAnswers.includes(optionId)
        ? selectedAnswers.filter(id => id !== optionId)
        : [...selectedAnswers, optionId];
      onSelectAnswer?.(newSelected);
    }
  };

  const handleCheck = () => {
    if (selectedAnswers.length === 0) return;
    onCheck?.(selectedAnswers);
  };

  const handleRetry = () => {
    onRetry?.();
  };

  const hideChrome = true;

  const getBorderColor = () => {
    if (!showResult) return 'border-transparent';
    return isCorrect ? 'border-green-400' : 'border-red-400';
  };

  return (
    <div
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : `border-2 ${getBorderColor()} shadow-sm`
      }`}
    >
      {/* Question */}
      {question && (
        <div
          className={`px-4 py-3 ${hideChrome ? '' : 'border-b border-gray-100'}`}
          style={hideChrome ? questionBgStyle : { ...questionBgStyle, ...(questionBgColor ? {} : { backgroundColor: '#f9fafb' }) }}
        >
          <p className="text-sm font-medium text-gray-800 leading-relaxed" style={questionTextStyle}>
            {question}
          </p>
        </div>
      )}

      {/* Options */}
      <div className="flex-1 p-3 overflow-y-auto space-y-2">
        {safeOptions.map((option, idx) => {
          const isSelected = selectedAnswers.includes(option.id);
          const correctAnswersSet = Array.isArray(correctAnswers) ? correctAnswers : null;
          const optionIsCorrect = options.find(o => o.id === option.id)?.isCorrect
            ?? (correctAnswersSet ? correctAnswersSet.includes(option.id) : undefined);
          const showCorrectness = showResult && (!studentView || correctAnswersSet !== null);

          // Determine option styling
          let optionBg = 'bg-white hover:bg-gray-50';
          let optionBorder = 'border-gray-200';
          let optionText = 'text-gray-700';
          let optionInlineBg = optionBaseBgStyle;
          let resultTextColor = null;

          if (isSelected && !showResult) {
            optionBg = 'bg-indigo-50';
            optionBorder = 'border-indigo-400';
            optionText = 'text-indigo-700';
            optionInlineBg = {};
          }

          if (showResult) {
            if (isSelected && isCorrect) {
              optionBg = 'bg-green-100';
              optionBorder = 'border-green-500';
              optionText = 'text-green-800';
              optionInlineBg = {};
              resultTextColor = '#166534';
            } else if (isSelected && !isCorrect) {
              optionBg = 'bg-red-100';
              optionBorder = 'border-red-500';
              optionText = 'text-red-800';
              optionInlineBg = {};
              resultTextColor = '#991b1b';
            } else if (showCorrectness && optionIsCorrect) {
              // Show correct answer when wrong (only for teacher/preview)
              optionBg = 'bg-green-50';
              optionBorder = 'border-green-300';
              optionText = 'text-green-700';
              optionInlineBg = {};
              resultTextColor = '#15803d';
            }
          }

          return (
            <button
              key={option.id || idx}
              onClick={() => handleOptionClick(option.id)}
              disabled={disabled || showResult}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 ${optionBorder} ${optionBg} transition-all ${
                !disabled && !showResult ? 'cursor-pointer' : 'cursor-default'
              }`}
              style={optionInlineBg}
            >
              {/* Selection indicator */}
              <div className="flex-shrink-0">
                {isSingleChoice ? (
                  isSelected ? (
                    <div className={`w-4 h-4 rounded-full ${showResult ? (isCorrect ? 'bg-green-500' : 'bg-red-500') : 'bg-indigo-500'} flex items-center justify-center`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  ) : (
                    <Circle size={16} className="text-gray-400" />
                  )
                ) : (
                  isSelected ? (
                    <CheckSquare size={16} className={showResult ? (isCorrect ? 'text-green-500' : 'text-red-500') : 'text-indigo-500'} />
                  ) : (
                    <Square size={16} className="text-gray-400" />
                  )
                )}
              </div>

              {/* Option text */}
              <span
                className={`flex-1 text-left text-sm ${optionText}`}
                style={resultTextColor ? { ...optionTextStyle, color: resultTextColor } : optionTextStyle}
              >
                {option.text || t('games.exercise.optionFallback', { index: idx + 1 })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Actions */}
      <div className={`px-3 py-2 flex items-center justify-end gap-2 ${hideChrome ? '' : 'border-t border-gray-100 bg-gray-50'}`}>
        {showResult && !isCorrect && allowRetry && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        )}

        {!showResult && !hideCheckButton && (
          <button
            onClick={handleCheck}
            disabled={selectedAnswers.length === 0}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              selectedAnswers.length > 0
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('games.common.check')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExerciseElement;
